/**
 * URL contract for saved-view display options. Params are the wire diff over the saved
 * baseline: absent inherits the baseline, and setting a param equal to the baseline
 * deletes it. The server validates the diff and may fall back on an unsupported value.
 * One writer covers every managed axis.
 */

import type {ShouldRevalidateFunctionArgs} from '@github-ui/react-router'

import type {ViewSort} from '../components/board/issues/config-payload'
import {sameVisibleFields} from '../contexts/views/view-configuration'

export const LAYOUT_PARAM = 'layout'
export const GROUP_BY_PARAM = 'group_by'
export const SWIMLANE_BY_PARAM = 'swimlane_by'
export const VISIBLE_FIELDS_PARAM = 'visible_fields'
export const SORTS_PARAM = 'sorts'
const PAGE_PARAM = 'page'

/** Every param a saved view's configuration occupies in the URL. */
export const SAVED_VIEW_URL_PARAMS = [
  LAYOUT_PARAM,
  GROUP_BY_PARAM,
  SWIMLANE_BY_PARAM,
  VISIBLE_FIELDS_PARAM,
  SORTS_PARAM,
] as const

export const LAYOUTS = ['list', 'board'] as const
export type Layout = (typeof LAYOUTS)[number]

export interface SavedViewUrlParams {
  layout: Layout
  groupBy: string | null
  /** Omitted by hosts that do not manage swimlane URL overrides. */
  swimlaneBy?: string | null
  /** Omitted by hosts that do not manage visible fields in the URL. */
  visibleFields?: readonly string[]
  /** Omitted by hosts that do not manage the board's sort in the URL. */
  sorts?: readonly ViewSort[]
}

export interface SavedViewUrlOverrides {
  layout?: Layout
  groupBy?: string | null
  swimlaneBy?: string | null
  sorts?: readonly ViewSort[]
}

/** Encodes sorts as the `sorts` param. An empty list encodes as blank, which asks for no sort. */
export function encodeSorts(sorts: readonly ViewSort[]): string {
  return sorts.map(({field, direction}) => `${field}-${direction}`).join(',')
}

/**
 * Decodes a `sorts` param, splitting each term on its last `-` since an identifier can contain
 * one. Malformed terms are dropped.
 */
export function decodeSorts(value: string): ViewSort[] {
  const sorts: ViewSort[] = []
  for (const term of value.split(',')) {
    const separator = term.lastIndexOf('-')
    const field = term.slice(0, separator).trim()
    const direction = term.slice(separator + 1).trim()
    if (separator > 0 && field && (direction === 'asc' || direction === 'desc')) sorts.push({field, direction})
  }
  return sorts
}

export function sameSorts(left: readonly ViewSort[], right: readonly ViewSort[]): boolean {
  return encodeSorts(left) === encodeSorts(right)
}

function isLayout(value: string | null): value is Layout {
  return value !== null && (LAYOUTS as readonly string[]).includes(value)
}

export function readSavedViewUrlOverrides(search: URLSearchParams): SavedViewUrlOverrides {
  const rawLayout = search.get(LAYOUT_PARAM)
  const groupBy = search.get(GROUP_BY_PARAM)?.trim() || null
  const swimlaneBy = search.get(SWIMLANE_BY_PARAM)?.trim() || null
  return {
    ...(isLayout(rawLayout) ? {layout: rawLayout} : {}),
    ...(search.has(GROUP_BY_PARAM) ? {groupBy} : {}),
    ...(search.has(SWIMLANE_BY_PARAM) ? {swimlaneBy} : {}),
    ...(search.has(SORTS_PARAM) ? {sorts: decodeSorts(search.get(SORTS_PARAM) ?? '')} : {}),
  }
}

type BoardAxes = {
  layout: Layout
  groupBy: string | null
  swimlaneBy: string | null
  sorts?: readonly ViewSort[]
}

function requestedAxis<V>(to: V | undefined, from: V | undefined, served: V, baseline: V): V {
  if (to === from) return served
  return to === undefined ? baseline : to
}

/**
 * Returns `served` with the axes a navigation still loading asks for. Both URLs are read,
 * because an absent param means two things: dropped from the URL on screen, the axis goes
 * back to the baseline, and absent from both, the axis keeps the served value, which may be
 * a server fallback (github/views#211).
 *
 * A List response serves no board axes, so a Board requested from List takes each axis from
 * the pending URL or the baseline, the same way `writeSavedViewOverrides` writes them.
 */
export function readRequestedAxes<T extends BoardAxes>(
  served: T,
  baseline: BoardAxes,
  current: URLSearchParams,
  pending: URLSearchParams,
): T {
  const from = readSavedViewUrlOverrides(current)
  const to = readSavedViewUrlOverrides(pending)
  const layout = requestedAxis(to.layout, from.layout, served.layout, baseline.layout)
  if (served.layout === 'list' && layout === 'board') {
    return {
      ...served,
      layout,
      groupBy: to.groupBy === undefined ? baseline.groupBy : to.groupBy,
      swimlaneBy: to.swimlaneBy === undefined ? baseline.swimlaneBy : to.swimlaneBy,
      ...(served.sorts && baseline.sorts ? {sorts: to.sorts ?? baseline.sorts} : {}),
    }
  }
  return {
    ...served,
    layout,
    groupBy: requestedAxis(to.groupBy, from.groupBy, served.groupBy, baseline.groupBy),
    swimlaneBy: requestedAxis(to.swimlaneBy, from.swimlaneBy, served.swimlaneBy, baseline.swimlaneBy),
    ...(served.sorts && baseline.sorts
      ? {sorts: requestedSorts(to.sorts, from.sorts, served.sorts, baseline.sorts)}
      : {}),
  }
}

// Like `requestedAxis`, but compares sorts by value, since each URL read decodes a new array.
function requestedSorts(
  to: readonly ViewSort[] | undefined,
  from: readonly ViewSort[] | undefined,
  served: readonly ViewSort[],
  baseline: readonly ViewSort[],
): readonly ViewSort[] {
  const unchanged = to === undefined || from === undefined ? to === from : sameSorts(to, from)
  if (unchanged) return served
  return to ?? baseline
}

/**
 * Returns the visible-fields override, or `undefined` to inherit the baseline. A
 * present-but-empty param is an explicit empty set (every field hidden). When `knownFields`
 * is given, identifiers outside it are ignored, so a stale or hand-edited link cannot mark the
 * view dirty with fields the board does not offer.
 *
 * Visible fields are client-side: they stay out of the route's query key (see
 * `planningViewSearchParams`), so toggling one never refetches. Only the board shows card
 * fields, so List ignores the param.
 */
export function readVisibleFieldsOverride(
  search: URLSearchParams,
  layout: Layout,
  knownFields?: ReadonlySet<string>,
): readonly string[] | undefined {
  if (layout !== 'board' || !search.has(VISIBLE_FIELDS_PARAM)) return undefined
  const fields = (search.get(VISIBLE_FIELDS_PARAM) ?? '')
    .split(',')
    .map(field => field.trim())
    .filter(field => field && (!knownFields || knownFields.has(field)))
  return [...new Set(fields)]
}

/**
 * Whether a saved view route reloads its response for a navigation. Visible fields are in the URL
 * but not in the request, so a change to them alone keeps the response on screen. Revalidating it
 * would refetch the board and re-key it, which drops every cell page loaded since.
 */
export function shouldRevalidateSavedView({
  currentUrl,
  nextUrl,
  defaultShouldRevalidate,
}: ShouldRevalidateFunctionArgs): boolean {
  if (!defaultShouldRevalidate) return false
  if (currentUrl.pathname !== nextUrl.pathname) return true

  const current = new URLSearchParams(currentUrl.search)
  const next = new URLSearchParams(nextUrl.search)
  if (current.get(VISIBLE_FIELDS_PARAM) === next.get(VISIBLE_FIELDS_PARAM)) return true

  for (const params of [current, next]) {
    params.delete(VISIBLE_FIELDS_PARAM)
    params.sort()
  }
  return current.toString() !== next.toString()
}

/**
 * Returns updated params after applying a partial change to the resolved shape. Each key
 * in `changes` is treated as the new effective value for that setting; keys you omit are
 * left as-is. An empty parameter explicitly clears a saved axis; an absent parameter
 * inherits the baseline. Swimlane, visible fields and sorts are written only when the baseline
 * includes them.
 *
 * Cross-setting rules baked in: layout=list drops every board-only param, and pagination
 * resets when the server's results would change.
 */
export function writeSavedViewOverrides(
  search: URLSearchParams,
  changes: Partial<SavedViewUrlParams>,
  baseline: SavedViewUrlParams,
  current: SavedViewUrlParams,
): URLSearchParams {
  const next = new URLSearchParams(search)
  // A visible-fields-only change leaves the axis params alone. Rewriting them from `current`
  // could add a server-defaulted axis to the URL, changing the query key and refetching.
  if (changes.layout !== undefined || changes.groupBy !== undefined || changes.swimlaneBy !== undefined) {
    writeAxes(next, changes, baseline, current)
  }
  if (baseline.visibleFields !== undefined) {
    writeVisibleFields(next, changes.layout ?? current.layout, changes.visibleFields, baseline.visibleFields)
  }
  if (baseline.sorts !== undefined) {
    writeSorts(next, changes.layout ?? current.layout, changes.sorts, baseline.sorts)
  }
  return next
}

// Writes layout, grouping, and swimlane, resetting pagination when the server's results change.
function writeAxes(
  params: URLSearchParams,
  changes: Partial<SavedViewUrlParams>,
  baseline: SavedViewUrlParams,
  current: SavedViewUrlParams,
): void {
  const layout = changes.layout ?? current.layout
  const groupBy = nextBoardAxis(changes.groupBy, current.layout, current.groupBy, baseline.groupBy)
  const swimlaneBy = nextBoardAxis(changes.swimlaneBy, current.layout, current.swimlaneBy, baseline.swimlaneBy) ?? null
  const managesSwimlanes = baseline.swimlaneBy !== undefined

  writeOrClear(params, LAYOUT_PARAM, layout, baseline.layout)
  writeBoardAxis(params, GROUP_BY_PARAM, layout, groupBy, baseline.groupBy)
  if (managesSwimlanes) writeBoardAxis(params, SWIMLANE_BY_PARAM, layout, swimlaneBy, baseline.swimlaneBy ?? null)

  const resultsChanged =
    layout !== current.layout ||
    onBoard(layout, groupBy) !== onBoard(current.layout, current.groupBy) ||
    (managesSwimlanes && onBoard(layout, swimlaneBy) !== onBoard(current.layout, current.swimlaneBy))
  if (resultsChanged) params.delete(PAGE_PARAM)
}

// A board axis the change leaves alone keeps its current value, or the baseline when arriving from List.
function nextBoardAxis<T>(change: T | undefined, currentLayout: Layout, current: T, baseline: T): T {
  if (change !== undefined) return change
  return currentLayout === 'board' ? current : baseline
}

// The value an axis actually has on the given layout: board axes do not apply to List.
function onBoard(layout: Layout, value: string | null | undefined): string | null {
  return layout === 'board' ? (value ?? null) : null
}

function writeBoardAxis(
  params: URLSearchParams,
  key: string,
  layout: Layout,
  value: string | null,
  baseline: string | null,
): void {
  if (layout === 'board') writeOrClear(params, key, value, baseline)
  else params.delete(key)
}

// Applies delete-when-equal-to-baseline for one param.
function writeOrClear(params: URLSearchParams, key: string, value: string | null, baseline: string | null): void {
  if (value === baseline) params.delete(key)
  else params.set(key, value ?? '')
}

// Omitted fields leave the param as-is on the board. Written sorted so equal sets produce the same URL.
function writeVisibleFields(
  params: URLSearchParams,
  layout: Layout,
  visibleFields: readonly string[] | undefined,
  baseline: readonly string[],
): void {
  if (layout !== 'board' || (visibleFields !== undefined && sameVisibleFields(visibleFields, baseline))) {
    params.delete(VISIBLE_FIELDS_PARAM)
  } else if (visibleFields !== undefined) {
    params.set(VISIBLE_FIELDS_PARAM, [...new Set(visibleFields)].sort().join(','))
  }
}

// Only the board uses `sorts`, so List drops it. Omitted `sorts` leave it unchanged.
function writeSorts(
  params: URLSearchParams,
  layout: Layout,
  sorts: readonly ViewSort[] | undefined,
  baseline: readonly ViewSort[],
): void {
  if (layout !== 'board' || (sorts !== undefined && sameSorts(sorts, baseline))) {
    params.delete(SORTS_PARAM)
  } else if (sorts !== undefined) {
    params.set(SORTS_PARAM, encodeSorts(sorts))
  }
}
